import { DatePipe } from "@angular/common";
import { ChangeDetectorRef, Component, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute, ParamMap } from "@angular/router";
import {
  amount as TotalLineAmount,
  generateCheckDigit,
  JobDto,
  JobLineDto,
  JobVisitDto,
  ListResultDto,
  newJobVisit,
  ResourceMasterDto,
  SearchDto,
  WebLayoutDto
} from "@shared/models";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { LookupValuePipe } from "@modules/common/pipes/lookup-value.pipe";
import { AppInjector } from "@app/app.module";
import { ContactPipe, DateExPipe, NoZeroPipe, PublishSubscribe } from "../../common";
import { DataTableDirective, SortOrder, TableColumn } from "../../common/components";
import { JobService, LookupListService, ProductSettingService } from "../../common/services";

export interface JobLineHelper extends JobLineDto {
  job: JobDto;
  visit: JobVisitDto;
}

const TotalInclVat = (row) => TotalLineAmount(row, true);
const TotalExclVat = (row) => TotalLineAmount(row, false);

type ListType = "all" | "open" | "closed";
@Component({
  templateUrl: "./line-list.component.html",
})
export class LineListComponent extends DataTableDirective implements OnInit, OnDestroy {
  dataList: ListResultDto<JobLineHelper>;
  showJobs: { [key: string]: string } = {
    all: "Job.AllJobs",
    open: "Job.OpenJobs",
    closed: "Job.ClosedJobs",
  };
  showJob: ListType = "open";
  baseColumns: TableColumn[] = [
    {
      group: "Job.Job",
      field: "job.jobId",
      header: "Job.JobID",
      defaultSort: SortOrder.Descending,
      class: "w-13"
    },
    {
      group: "JobLine.JobLine",
      field: "lineId",
      header: "JobLine.LineID",
      defaultSort: SortOrder.None,
      class: "w-8",
      skipFilter: true
    },
    {
      group: "Job.Job",
      field: "job.typeId",
      header: "Job.TypeID",
      defaultSort: SortOrder.Ascending,
      class: "w-8"
    },
    {
      group: "Job.Job",
      field: "job.statusId",
      header: "Job.StatusID",
      defaultSort: SortOrder.Ascending,
      class: "w-8",
      hidden: true
    },
    {
      field: "job.logged",
      header: "Job.Logged",
      group: "Job.Job",
      defaultSort: SortOrder.Ascending,
      pipe: [new DatePipe("en-ZA"), "MMM dd"],
      class: "w-8",
      hidden: true
    },

    {
      group: "JobLine.JobLine",
      field: "receipt",
      header: "JobLine.Receipt",
      defaultSort: SortOrder.Ascending,
      class: "w-8"
    },
    {
      group: "JobLine.JobLine",
      field: "invoice",
      header: "Job.Invoice",
      defaultSort: SortOrder.Ascending,
      class: "w-8",
      hidden: true
    },
    {
      group: "JobLine.JobLine",
      field: "typeId",
      header: "JobLine.TypeID",
      defaultSort: SortOrder.Ascending,
      class: "w-8",
      hidden: true
    },
    {
      group: "JobLine.JobLine",
      field: "itemId",
      header: "JobLine.ItemID",
      defaultSort: SortOrder.Ascending,
      class: "w-8"
    },
    {
      group: "JobLine.JobLine",
      field: "line1",
      header: "JobLine.Description",
      defaultSort: SortOrder.Ascending,
      class: "w-20"
    },

    // Resource
    {
      group: "Job.Resource",
      field: "job.resourceId",
      header: "Job.Resource",
      defaultSort: SortOrder.Ascending,
      class: "w-8",
      hidden: true
    },
    {
      group: "Job.Resource",
      columnId: "Job.ResourceCompany", // NOTE: include both a unique'columnId' & 'field' to prevent erroneous column selection
      field: "resourceId",
      header: "Job.ResourceCompany",
      defaultSort: SortOrder.None,
      class: "w-8",
      hidden: true,
      lookupFunc: (item: ResourceMasterDto) => item?.contact?.companyName,
      lookupLong: "ResourceMaster",
    },
    {
      group: "Job.Resource",
      columnId: "Job.ResourceName",
      field: "resourceId",
      header: "Job.ResourceName",
      defaultSort: SortOrder.None,
      class: "w-8",
      hidden: true,
      lookupFunc: (item: ResourceMasterDto) => item?.contact?.firstName + ' ' + item?.contact?.lastName,
      lookupLong: "ResourceMaster",
    },
    {
      group: "Job.Resource",
      columnId: "Stock.Warehouse",
      field: "warehouseId",
      header: "Stock.Warehouse",
      defaultSort: SortOrder.None,
      class: "w-8",
      hidden: true,
      lookupShort: "WarehouseMaster",
    },

    {
      group: "Job.Visits",
      field: "visit.resourceId",
      header: "JobLine.VisitResource",
      defaultSort: SortOrder.Ascending,
      class: "w-8",
      hidden: true
    },
    {
      group: "Job.Visits",
      field: "visit.statusId",
      header: "JobLine.VisitStatus",
      defaultSort: SortOrder.Ascending,
      class: "w-8",
      hidden: true
    },
    {
      group: "Job.Visits",
      field: "job.visitDate",
      header: "Job.Visited",
      defaultSort: SortOrder.Ascending,
      pipe: new DateExPipe(),
      class: "w-8",
      hidden: true
    },
    {
      group: "Job.Job",
      field: "job.completed",
      header: "Job.Completed",
      defaultSort: SortOrder.Descending,
      pipe: new DateExPipe(),
      class: "w-8",
      hidden: true
    },
    // {
    //   group: "Job.Job",
    //   field: "job.machine.modelId",
    //   header: "Machine.ModelID",
    //   defaultSort: SortOrder.Ascending,
    //   class: "w-10",
    //   hidden: true
    // },

    // MACHINE
    {
      field: "job.machine.machineId",
      header: "Machine.MachineID",
      group: "Job.Machine",
      defaultSort: SortOrder.Ascending,
      class: "w-15",
      hidden: true,
    },
    {
      field: "job.machine.modelId",
      header: "Machine.ModelID",
      group: "Job.Machine",
      defaultSort: SortOrder.Ascending,
      class: "w-15",
    },
    {
      field: "job.machine.serialNumber",
      header: "Machine.Serial",
      group: "Job.Machine",
      defaultSort: SortOrder.Ascending,
      class: "w-10",
      hidden: true,
    },
    {
      field: "job.machine.fields.0",
      header: "Machine.Field1",
      group: "Job.Machine",
      defaultSort: SortOrder.None,
      class: "w-8",
      hidden: true,
    },
    {
      field: "job.machine.fields.1",
      header: "Machine.Field2",
      group: "Job.Machine",
      defaultSort: SortOrder.None,
      class: "w-8",
      hidden: true,
    },
    {
      field: "job.machine.fields.2",
      header: "Machine.Field3",
      group: "Job.Machine",
      defaultSort: SortOrder.None,
      class: "w-8",
      hidden: true,
    },
    {
      field: "job.machine.fields.3",
      header: "Machine.Field4",
      group: "Job.Machine",
      defaultSort: SortOrder.None,
      class: "w-8",
      hidden: true,
    },
    {
      field: "job.machine.fields.4",
      header: "Machine.Field5",
      group: "Job.Machine",
      defaultSort: SortOrder.None,
      class: "w-8",
      hidden: true,
    },
    {
      field: "job.machineCount",
      header: "Job.MachineCount",
      group: "Job.Machine",
      defaultSort: SortOrder.Descending,
      class: "w-5",
      hidden: true,
    },
    {
      field: "job.machine.model.description",
      header: "Machine.MachineName",
      group: "Job.Machine",
      defaultSort: SortOrder.Descending,
      class: "w-15",
      hidden: true,
    },
    {
      field: "job.machine.model.categories.0",
      queryParam: "machine.category1id",
      header: "Machine.Category1",
      group: "Job.Machine",
      defaultSort: SortOrder.Descending,
      class: "w-5",
      hidden: true,
      pipe: [new LookupValuePipe(AppInjector.get(LookupListService), this.changeDetector), 'CodeMachineCategory']
    },
    {
      field: "job.machine.model.categories.1",
      queryParam: "machine.category2id",
      header: "Machine.Category2",
      group: "Job.Machine",
      defaultSort: SortOrder.Descending,
      class: "w-5",
      hidden: true,
      pipe: [new LookupValuePipe(AppInjector.get(LookupListService), this.changeDetector), 'CodeMachineCategory']
    },
    {
      field: "job.machine.dateOfPurchase",
      header: "Machine.DateOfPurchase",
      group: "Job.Machine",
      defaultSort: SortOrder.Descending,
      class: "w-8",
      pipe: new DateExPipe(),
      hidden: true,
      skipFilter: true,
    },
    {
      field: "job.machine.dealerBranchId",
      header: "Machine.DealerID",
      group: "Job.Machine",
      defaultSort: SortOrder.None,
      class: "w-10",
      hidden: true,
      lookupShort: "DealerBranch",
    },

    // Job Line
    {
      group: "JobLine.JobLine",
      field: "quantityOrdered",
      header: "JobLine.Ordered",
      defaultSort: SortOrder.Descending,
      class: "w-8 text-right",
      pipe: [new NoZeroPipe(), "1.0"],
      hidden: true
    },
    {
      group: "JobLine.JobLine",
      field: "quantityDelivered",
      header: "JobLine.Delivered",
      defaultSort: SortOrder.Descending,
      class: "w-8 text-right",
      pipe: [new NoZeroPipe(), "1.0"],
      hidden: true
    },
    {
      group: "JobLine.JobLine",
      field: "price",
      header: "JobLine.Price",
      defaultSort: SortOrder.Descending,
      skipFilter: true,
      class: "w-10 text-right",
      // pipe: [new NoZeroPipe(), "1.2-2"],
      hidden: true
    },
    {
      group: "JobLine.JobLine",
      field: "price-excl",
      header: "JobLine.AmountExcl",
      skipFilter: true,
      defaultSort: SortOrder.None,
      class: "w-10 text-right",
      // pipe: [new NoZeroPipe(), "1.2-2"],
      hidden: true,
      converter: TotalExclVat,
    },
    {
      group: "JobLine.JobLine",
      field: "price-incl",
      header: "JobLine.AmountIncl",
      skipFilter: true,
      defaultSort: SortOrder.None,
      class: "w-10 text-right",
      // pipe: [new NoZeroPipe(), "1.2-2"],
      hidden: true,
      converter: TotalInclVat,
    },
    {
      group: "JobLine.JobLine",
      field: "paid",
      header: "JobLine.Paid",
      defaultSort: SortOrder.Descending,
      class: "w-10 text-right",
      // pipe: [new NoZeroPipe(), "1.2-2"]
    },
    {
      group: "JobLine.JobLine",
      field: "unpaid",
      header: "JobLine.Unpaid",
      defaultSort: SortOrder.Descending,
      class: "w-10 text-right",
      hidden: true,
      // pipe: [new NoZeroPipe(), "1.2-2"]
    },
    {
      group: "JobLine.JobLine",
      field: "requested",
      header: "JobLine.PaidDate",
      defaultSort: SortOrder.Descending,
      pipe: new DateExPipe(),
      class: "w-13"
    },

    // Contract
    // TODO: Add to DTO and tweak performance first
    // {
    //   group: "Contract.ContractID",
    //   field: "job.contract.typeId",
    //   header: "Contract.TypeID",
    //   defaultSort: SortOrder.None,
    //   class: "w-8",
    //   hidden: true,
    //   skipFilter: true,
    // },

    // Metrics
    {
      group: "Job.Metrics",
      field: "job.metrics.decimals.0",
      header: "Metric.Description1",
      defaultSort: SortOrder.None,
      class: "w-8",
      hidden: true,
      skipFilter: true,
      pipe: new NoZeroPipe()
    },
    {
      group: "Job.Metrics",
      field: "job.metrics.decimals.1",
      header: "Metric.Description2",
      defaultSort: SortOrder.None,
      class: "w-8",
      hidden: true,
      skipFilter: true,
      pipe: new NoZeroPipe()
    },
    {
      group: "Job.Metrics",
      field: "job.metrics.decimals.2",
      header: "Metric.Description3",
      defaultSort: SortOrder.None,
      class: "w-8",
      hidden: true,
      skipFilter: true,
      pipe: new NoZeroPipe()
    },
    {
      group: "Job.Metrics",
      field: "job.metrics.decimals.3",
      header: "Metric.Description4",
      defaultSort: SortOrder.None,
      class: "w-8",
      hidden: true,
      skipFilter: true,
      pipe: new NoZeroPipe()
    },
    {
      group: "Job.Metrics",
      field: "job.metrics.decimals.4",
      header: "Metric.Description5",
      defaultSort: SortOrder.None,
      class: "w-8",
      hidden: true,
      skipFilter: true,
      pipe: new NoZeroPipe()
    },
    {
      group: "Job.Metrics",
      field: "job.metrics.decimals.5",
      header: "Metric.Description6",
      defaultSort: SortOrder.None,
      class: "w-8",
      hidden: true,
      skipFilter: true,
      pipe: new NoZeroPipe()
    },
    {
      group: "Job.Metrics",
      field: "job.metrics.decimals.6",
      header: "Metric.Description7",
      defaultSort: SortOrder.None,
      class: "w-8",
      hidden: true,
      skipFilter: true,
      pipe: new NoZeroPipe()
    },
    {
      group: "Job.Metrics",
      field: "job.metrics.decimals.7",
      header: "Metric.Description8",
      defaultSort: SortOrder.None,
      class: "w-8",
      hidden: true,
      skipFilter: true,
      pipe: new NoZeroPipe()
    },
    {
      group: "Job.Metrics",
      field: "job.metrics.decimals.8",
      header: "Metric.Description9",
      defaultSort: SortOrder.None,
      class: "w-8",
      hidden: true,
      skipFilter: true,
      pipe: new NoZeroPipe()
    },
    {
      group: "Job.Metrics",
      field: "job.metrics.decimals.9",
      header: "Metric.Description10",
      defaultSort: SortOrder.None,
      class: "w-8",
      hidden: true,
      skipFilter: true,
      pipe: new NoZeroPipe()
    },

    // DEALER
    {
      field: "job.machine.dealerBranchId",
      header: "Machine.DealerID",
      group: "Machine.DealerID",
      defaultSort: SortOrder.None,
      class: "w-10",
      hidden: true,
      lookupShort: "DealerBranch",
    },
    {
      group: "Machine.DealerID",
      field: "job.dealerContact",// data property
      header: "Job.Dealer",
      defaultSort: SortOrder.None,
      class: "w-10",
      skipFilter: true,
      hidden: true,
      pipe: new ContactPipe()
    },
    {
      group: "Machine.DealerID",
      field: "job.dealerContact.officeHours",// data property
      header: "Contact.OfficeHours",
      defaultSort: SortOrder.None,
      class: "w-10",
      skipFilter: true,
      hidden: true,
    },
    {
      group: "Machine.DealerID",
      field: "job.dealerContact.emailAddress",// data property
      header: "Contact.Email",
      defaultSort: SortOrder.None,
      class: "w-10",
      skipFilter: true,
      hidden: true,
    },
  ];

  dateColumns: string[] = [
    "job.logged",
    "job.completed",
    "requested",
    "job.visitDate"
  ];

  layoutColumns: { [id: string]: TableColumn };
  jobId: string;
  codJobs = false;

  constructor(
    private jobService: JobService,
    public route: ActivatedRoute,
    private pubSub: PublishSubscribe,
    productSetting: ProductSettingService,
    private changeDetector: ChangeDetectorRef
  ) {
    super("JobLineList");
    this.codJobs = productSetting.booleanValue("JobGlobalFilter");
  }

  protected setVars(params: ParamMap): void {
    this.showJob = (params.get("mode") as ListType) || "open";
    super.setVars(params);
  }

  jobLink(row: JobLineHelper | string): any[] {
    const jobId = typeof row === "object" ? row.job.jobId : row;
    if (this.codJobs) {
      return [
        "../../public",
        jobId,
        generateCheckDigit(jobId),
        { inapp: "true" }
      ];
    } else {
      return ["../../", jobId];
    }
  }

  innerSearch(
    page: number,
    size: number,
    columns: TableColumn[]
  ): Observable<ListResultDto<JobLineHelper>> {
    let sortField = "!job.jobId";
    const sortCol = columns.find(c => c.sortOrder > SortOrder.None);
    if (sortCol) {
      sortField =
        (sortCol.sortOrder === SortOrder.Descending ? "!" : "") +
        (sortCol.columnId || sortCol.field);
    }

    const jobSearch: SearchDto = {
      pageNumber: page,
      pageSize: size,
      orderBy: [sortField],
      filters: {}
    };

    switch (this.showJob) {
      case "open":
        jobSearch.filters["job.closed"] = "false";
        break;
      case "closed":
        jobSearch.filters["job.closed"] = "true";
        break;
    }

    for (const prop of columns) {
      if (prop.filter) {
        const id = prop.columnId || prop.field;
        jobSearch.filters[id] = prop.filter;
      }
    }

    return this.jobService.queryJobLines(jobSearch)
      .pipe(map(list => {
        const items: JobLineHelper[] = [];
        for (const job of list.items) {
          const visit = job.visits.length ? job.visits[0] : newJobVisit();
          items.push(...job.lines.map(l => ({ ...l, job, visit })));
        }

        return {
          items,
          totalResults: list.totalResults
        };
      }));
  }

  showResource(resourceId: string): boolean {
    this.pubSub.publish("resource_modal", resourceId);
    return false;
  }

  layoutExtras(data: any) {
    data.mode = this.showJob || "open";
  }

  setupColumns(): void {
    this.columns = [...this.baseColumns];
  }

  setLayout(layout: WebLayoutDto) {
    if (layout && layout.data.mode) {
      this.showJob = layout.data.mode;
    }
    super.setLayout(layout);
  }
}
